/**
 * Profile completeness utility functions
 */

// Required fields for Partners (companies)
const PARTNER_REQUIRED_FIELDS = [
  'companyName',
  'phoneNumber',
  'companyAddress',
  'companyCity',
  'companyPostCode',
  'vatNumber',
];

// Required fields for Clients (only phone number is mandatory)
const CLIENT_REQUIRED_FIELDS = [
  'phoneNumber',
];

/**
 * Check if a profile field value is empty
 * @param {any} value - The field value to check
 * @returns {boolean} - True if the field is empty
 */
function isFieldEmpty(value) {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  return false;
}

/**
 * Get required fields based on user role
 * @param {Object} user - The user object from the API
 * @returns {string[]} - Array of required field names for the user's role
 */
function getRequiredFieldsForRole(user) {
  if (!user || typeof user !== 'object') {
    return [...PARTNER_REQUIRED_FIELDS];
  }

  // Partners need all company fields including VAT
  if (user.role === 'partner') {
    return [...PARTNER_REQUIRED_FIELDS];
  }

  // Clients and others only need phone number
  return [...CLIENT_REQUIRED_FIELDS];
}

/**
 * Check if the user profile is complete
 * @param {Object} user - The user object from the API
 * @returns {boolean} - True if all required fields for the user's role are filled
 */
export function isProfileComplete(user) {
  if (!user || typeof user !== 'object') {
    return false;
  }

  const requiredFields = getRequiredFieldsForRole(user);
  return requiredFields.every((field) => !isFieldEmpty(user[field]));
}

/**
 * Get an array of missing profile field names
 * @param {Object} user - The user object from the API
 * @returns {string[]} - Array of missing field names for the user's role
 */
export function getMissingProfileFields(user) {
  if (!user || typeof user !== 'object') {
    return [...PARTNER_REQUIRED_FIELDS];
  }

  const requiredFields = getRequiredFieldsForRole(user);
  return requiredFields.filter((field) => isFieldEmpty(user[field]));
}

/**
 * Check if a specific field is required for the user's role
 * @param {Object} user - The user object from the API
 * @param {string} fieldName - The field name to check
 * @returns {boolean} - True if the field is required for the user's role
 */
export function isFieldRequiredForRole(user, fieldName) {
  if (!user || typeof user !== 'object') {
    return false;
  }

  const requiredFields = getRequiredFieldsForRole(user);
  return requiredFields.includes(fieldName);
}
